import React, { FC } from 'react'
import text from 'styles/utility/text.module.scss'
import { OptionType } from 'types/schedule'
import { Headline } from 'components/Typography'
import { Spacer } from 'components/Utility'
import { CardGreyHeadline, CardOutlined, CardOutlinedInner } from 'components/Card'
import { PreviewImage } from 'components/Image'
import { PRODUCT_TAX_RATE } from 'constants/tax'
import { ALLERGENS } from 'constants/allergens'
import { Label } from 'types/label'
import { BreadLabel } from 'types/bread'
import { WysiwygDisplayWrapper } from 'components/Form'
import { FlexRow } from 'components/Flex'
import { useSelector } from 'react-redux'
import { State } from 'index'
import { getIsAdmin } from 'reducks/auth/selectors'

type Props = {
  name: string
  shortName: string
  imageUrls: string[]
  description: string
  categoryName: string
  price: number
  allergens: string[]
  isSetAllergens: boolean
  isTypeDaily: boolean
  isTypePreorder: boolean
  isTypeDelivery: boolean
  status: 'public' | 'private'
  options: OptionType[]
  breadLabels: BreadLabel[]
  labels: Label[]
}

const CommonSettings: FC<Props> = (props) => {
  const {
    name,
    shortName,
    imageUrls,
    description,
    categoryName,
    price,
    allergens,
    isSetAllergens,
    isTypeDaily,
    isTypePreorder,
    isTypeDelivery,
    status,
    options,
    breadLabels,
    labels,
  } = props

  const isAdmin = useSelector((state: State) => getIsAdmin(state))

  return (
    <>
      <Headline headline={'共通設定'} />
      <Spacer size={20} />
      <CardOutlined>
        <CardGreyHeadline headline={'商品名'} isTop={true} />
        <Spacer size={8} />
        <CardOutlinedInner>{name}</CardOutlinedInner>
        <Spacer size={8} />
        {shortName !== '' && (
          <>
            <CardGreyHeadline headline={'商品の短縮名'} />
            <Spacer size={8} />
            <CardOutlinedInner>{shortName}</CardOutlinedInner>
            <Spacer size={8} />
          </>
        )}
        <CardGreyHeadline headline={'商品画像'} />
        <Spacer size={8} />
        <CardOutlinedInner>
          <FlexRow justifyContent={'start'} wrap={'wrap'} itemNum={'two'}>
            {imageUrls.map((imageUrl) => (
              <PreviewImage key={imageUrl} imageUrl={imageUrl} alt={name} />
            ))}
          </FlexRow>
        </CardOutlinedInner>
        <Spacer size={8} />
        <CardGreyHeadline headline={'商品説明'} />
        <Spacer size={8} />
        <CardOutlinedInner>
          <WysiwygDisplayWrapper content={description} />
        </CardOutlinedInner>
        <Spacer size={8} />
        <CardGreyHeadline headline={'カテゴリー'} />
        <Spacer size={8} />
        <CardOutlinedInner>{categoryName}</CardOutlinedInner>
        <Spacer size={8} />
        <CardGreyHeadline headline={'販売単価'} />
        <Spacer size={8} />
        <CardOutlinedInner>
          {price.toLocaleString()}円<br />
          アプリ上ではお客さまに
          <span className={text.color_orange_main}>
            {Math.floor(price * PRODUCT_TAX_RATE).toLocaleString()}円(税込)
          </span>
          で表示されます。
        </CardOutlinedInner>
        <Spacer size={8} />
        <CardGreyHeadline headline={'アレルギー情報'} />
        <Spacer size={8} />
        <CardOutlinedInner>
          {isSetAllergens ? (
            allergens.length === 0 ? (
              <p>なし</p>
            ) : (
              allergens.map((allergen) => {
                const ALLERGEN = ALLERGENS.find((ALLERGEN) => ALLERGEN.value === allergen)
                if (!ALLERGEN) return null
                return <p>・{ALLERGEN.label}</p>
              })
            )
          ) : (
            <p>アレルギー情報なし</p>
          )}
        </CardOutlinedInner>
        <Spacer size={8} />
        <CardGreyHeadline headline={'注文タイプ'} />
        <Spacer size={8} />
        <CardOutlinedInner>
          {isTypeDaily && <span>・当日取り置き</span>}
          {isTypeDaily && isTypePreorder && <br />}
          {isTypePreorder && <span>・事前予約</span>}
          {(isTypeDaily || isTypePreorder) && isTypeDelivery && <br />}
          {isTypeDelivery && <span>・取り寄せ</span>}
        </CardOutlinedInner>
        <Spacer size={8} />
        {isAdmin && (
          <>
            <CardGreyHeadline headline={'商品紹介タグ'} />
            <Spacer size={8} />
            <CardOutlinedInner>
              {breadLabels
                .map((breadLabel) => {
                  const label = labels.find((label) => label.ref?.id === breadLabel.label_ref.id)
                  if (!label) return
                  return label.name
                })
                .join(', ')}
            </CardOutlinedInner>
            <Spacer size={8} />
          </>
        )}
        <CardGreyHeadline headline={'出品ステータス'} />
        <Spacer size={8} />
        <CardOutlinedInner>
          {status === 'public' && <span>出品中</span>}
          {status === 'private' && <span>出品停止</span>}
        </CardOutlinedInner>
        <Spacer size={8} />
        {options.map((option) => (
          <React.Fragment key={option.key}>
            <CardGreyHeadline headline={option.key} />
            <Spacer size={8} />
            <CardOutlinedInner>
              {option.key === '商品サイズ' ? (
                <>
                  <span>幅 : {option.value.split('x')[0]}mm</span>
                  <br />
                  <span>奥行き : {option.value.split('x')[1]}mm</span>
                  <br />
                  <span>高さ : {option.value.split('x')[2]}mm</span>
                </>
              ) : (
                <span>{option.value}</span>
              )}
            </CardOutlinedInner>
            <Spacer size={8} />
          </React.Fragment>
        ))}
      </CardOutlined>
    </>
  )
}

export default CommonSettings
